import { Stack, type Theme, useMediaQuery } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import PageContainer from '/@/components/bayer/page-container';
import EmailVerificationAlert from '/@/components/email-verification-alert';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import Header from '../../../../components/bayer/header';
import CreateRecommendationModal from '../../rv/components/create-recommendation-modal';
import FieldSection from '../../rv/components/field-section';
import LocationsSelectorModal from '../../rv/components/locations-selector-modal';
import UserUIAlertAndModal from '../../rv/components/onboarding-modal-and-alert/user-ui-alert-and-modal';
import ProductsSection from '../../rv/components/product-section';
import SuggestionsSection from '../../rv/components/suggestions-section';
import YieldEstimationModal from '../../rv/components/yield-estimation-modal';
import LocationsSelector from '../../rv/components/zone-selector';
import ActivitySection from '../components/activity-section';

/**
 * Home for the generic / commercial internal profile (`user_type = other`).
 *
 * Structurally the RV home — same sections, all existing components — but scoped to
 * the user's own node instead of their growers, and worded for someone working their
 * own fields (`audience`).
 *
 * The growers/RVs sections and `OnboardingModalAndAlert` (the RV <-> Advisor
 * invitation surface) are absent by not being rendered, so this profile cannot regain
 * them by accident. `UserUIAlertAndModal` stays: despite its folder it is the generic
 * announcement banner, not part of the advisor flow.
 */
const OtherHome = () => {
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openLocationSelectorModal, setOpenLocationSelectorModal] = useState(false);
    const [openCreateRecommendationModal, setOpenCreateRecommendationModal] = useState(false);
    const [openYieldEstimationModal, setOpenYieldEstimationModal] = useState(false);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);

    const user = useSelector(getUser);
    const country = useSelector(getSessionCountry);
    const userNodeQuery = useOwnUserNode();
    const userNodeId = userNodeQuery.data?.id;

    // AR-only, matching the RV home.
    const hideLocationsSelector = country?.code !== 'AR';

    return (
        <PageContainer
            boxProps={{ sx: { background: '#F5F5F5' } }}
            header={
                <Header
                    title={
                        <>
                            {_('Hola, ')}
                            <span className='mp-mask'>{user?.first_name || ''}</span>
                        </>
                    }
                    subtitle={_('Gestioná tus lotes y potenciá el rendimiento de tus campos.')}
                    showSkeleton={userNodeQuery.isValidating}
                    actions={
                        hideLocationsSelector
                            ? []
                            : [
                                  <LocationsSelector
                                      key='locations-selector'
                                      onClick={() => setOpenLocationSelectorModal(true)}
                                  />,
                              ]
                    }
                />
            }
            boxChildren={
                <>
                    <EmailVerificationAlert />
                    <LocationsSelectorModal
                        open={openLocationSelectorModal && !hideLocationsSelector}
                        setOpen={setOpenLocationSelectorModal}
                    />
                    <CreateRecommendationModal
                        open={openCreateRecommendationModal}
                        setOpen={setOpenCreateRecommendationModal}
                        selectedField={selectedField}
                        setSelectedField={setSelectedField}
                        entrySource='other-home'
                    />
                    <YieldEstimationModal open={openYieldEstimationModal} setOpen={setOpenYieldEstimationModal} />
                </>
            }
        >
            <Stack my={3}>
                <UserUIAlertAndModal />
            </Stack>
            <Stack className='mp-mask' spacing={4}>
                <Grid2 spacing={3} container mx={{ sm: '-12px !important' }} alignItems='stretch'>
                    <Grid2 xs={12} sm={5} md={6} lg={4} sx={[isSmOrLower && { px: 0 }, { display: 'flex' }]}>
                        <ActivitySection setOpenCreateRecommendationModal={setOpenCreateRecommendationModal} />
                    </Grid2>
                    <Grid2 xs={12} sm={7} md={6} lg={4} sx={[isSmOrLower && { px: 0 }, { display: 'flex' }]}>
                        <ProductsSection
                            setOpenLocationSelectorModal={setOpenLocationSelectorModal}
                            audience='own-fields'
                        />
                    </Grid2>
                    <Grid2 xs={12} sm={12} md={12} lg={4} sx={[isSmOrLower && { px: 0 }, { display: 'flex' }]}>
                        <SuggestionsSection
                            setOpenYieldEstimationModal={setOpenYieldEstimationModal}
                            audience='own-fields'
                        />
                    </Grid2>
                </Grid2>
                <FieldSection
                    userNodeId={userNodeId}
                    title={_('Lotes')}
                    subtitle={_('Buscá un lote por nombre y generá una recomendación rápida y precisa.')}
                    nodeScope={getNodeScopeFilters('myFields')}
                    emptyMessage={_('Todavía no tenés lotes cargados')}
                />
            </Stack>
        </PageContainer>
    );
};

export default OtherHome;
